import React, { useCallback, useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import axiosInstant from '../../api'
import { rmnAuditReportEndpoint } from '../../api/endpoints'
import { FontLoader } from '../../components/PdpAudit/PdpAuditTool'
import {
  POLL_INTERVAL_MS,
  ReportDialogBody,
  RmnAuditReportRow,
} from '../../components/PdpAudit/auditReportShared'
import { PAGE_BG } from '../../components/ProposalFlow/theme'

// Deep-linkable report view — /intelligence/pdp-audit/reports/:reportId and
// the legacy /portal/:brandSlug/pdp-audit/reports/:reportId mount. Survives the
// PDP Audit page's retirement (TDC-1499 phase 3): report links from the
// Catalog pages, emails and old bookmarks all land here. Fetches one report
// and polls while it's still generating.
export default function RmnAuditReportPage() {
  const { reportId } = useParams()
  const [row, setRow] = useState<RmnAuditReportRow | null>(null)
  const [error, setError] = useState<string | null>(null)

  const id = parseInt(reportId || '', 10)

  const fetchReport = useCallback(async () => {
    if (!id) {
      setError('Report not found')
      return
    }
    try {
      const res = await axiosInstant.get(rmnAuditReportEndpoint(id))
      setRow(res.data as RmnAuditReportRow)
      setError(null)
    } catch (err: any) {
      setError(
        err?.response?.status === 404
          ? 'Report not found'
          : 'Failed to load the report. Please try again.',
      )
    }
  }, [id])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  const generating = row?.status === 'generating'
  useEffect(() => {
    if (!generating) return undefined
    const timer = setInterval(fetchReport, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [generating, fetchReport])

  return (
    <Box className="flex-1" sx={{ background: PAGE_BG, minHeight: '60vh' }}>
      <FontLoader />
      {!row && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={26} />
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 10 }}>
          {error}
        </Typography>
      )}
      {row && generating && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <CircularProgress size={26} sx={{ color: 'var(--rmn-primary-dark, #A58450)', mb: 2 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'var(--text)' }}>
            Generating your report…
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-2)', mt: 0.5 }}>
            This usually takes a few minutes. The page refreshes automatically.
          </Typography>
        </Box>
      )}
      {row && row.status === 'error' && (
        <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 10 }}>
          {row.generation_error || 'Report generation failed. Please start a new report.'}
        </Typography>
      )}
      {row && row.status === 'ready' && row.report && <ReportDialogBody row={row} />}
    </Box>
  )
}
